import { Form } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useStore } from 'store/useStore'
import { EmployeeKeys, MIN_NAME_LENGTH } from 'sections/LKSection/modules/EmployeeModule/data'
import { getEmployeePageKeys } from 'sections/LKSection/modules/EmployeeModule/utils/getKeys'
import { countries } from 'sections/LKSection/modules/UserModule/data'
import { UserModuleKeys } from 'sections/LKSection/modules/UserModule/types'
import { SNILS_LENGTH } from 'sections/LKSection/modules/UserModule/utils/getSnilsError'
import { BgBox } from 'sections/LKSection/shared/components/UI/BgBox/BgBox'
import { CountryPhoneInput } from 'components/CountryPhoneInput/CountryPhoneInput'
import { Button, Flex, Input } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { REPLACE_NOT_NUMBERS_REGEXP } from 'shared/constants/regExp'

import { validateAddEmployee } from './validate'

const getKey = (key: string) => `${EmployeeKeys.addEmployeePage}.${key}`
const getPassportStepKey = (key: string) => `${UserModuleKeys.passportStep}.${key}`
const getProfilePageKey = (key: string) => `${UserModuleKeys.profilePage}.${key}`

type AddEmployeeFormType = {
  lastName: string
  firstName: string
  snils: string
}

export const PersonalEmployeeDataStep = observer(() => {
  const { viewEmployeeUiStore: uiStore } = useStore()

  const [form] = Form.useForm()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    form.setFieldsValue({
      lastName: uiStore.lastName,
      firstName: uiStore.firstName,
      snils: uiStore.snils,
    })
  }, [])

  const addEmployeeHandler = async () => {
    if (validateAddEmployee.validate(uiStore)) {
      setLoading(true)
      const result = await uiStore.addEmployee()

      if (result) {
        uiStore.setSelectedStep(uiStore.selectedStep + 1)
      }

      setLoading(false)
    }
  }

  return (
    <BgBox>
      <Flex vertical gap={24} style={{ width: '600px' }}>
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item<AddEmployeeFormType>
            label={t(getPassportStepKey('Фамилия'))}
            name="lastName"
            rules={[
              { min: MIN_NAME_LENGTH, message: t(getSharedKey('minLength'), { count: MIN_NAME_LENGTH }) },
              { required: true, message: t(getSharedKey('required')) },
            ]}
          >
            <Input
              value={uiStore.lastName}
              onChange={(e) => uiStore.setLastName(e.target.value)}
              maxLength={100}
              placeholder={t(getKey('Введите фамилию'))}
            />
          </Form.Item>

          <Form.Item<AddEmployeeFormType>
            label={t(getPassportStepKey('Имя'))}
            name="firstName"
            rules={[
              { min: MIN_NAME_LENGTH, message: t(getSharedKey('minLength'), { count: MIN_NAME_LENGTH }) },
              { required: true, message: t(getSharedKey('required')) },
            ]}
          >
            <Input
              value={uiStore.firstName}
              onChange={(e) => uiStore.setFirstName(e.target.value)}
              maxLength={100}
              placeholder={t(getKey('Введите имя'))}
            />
          </Form.Item>

          <Form.Item<AddEmployeeFormType>
            label={`${t(getProfilePageKey('СНИЛС'))} (${t(getSharedKey('notRequired'))})`}
            name={'snils'}
          >
            <Input
              value={uiStore.snils}
              onChange={(e) => {
                const onlyNumbers = e.target.value.replace(REPLACE_NOT_NUMBERS_REGEXP, '')

                uiStore.setSnils(onlyNumbers)
                form.setFieldValue('snils', onlyNumbers)
              }}
              maxLength={SNILS_LENGTH}
              placeholder={t(getEmployeePageKeys('Введите СНИЛС'))}
            />
          </Form.Item>

          <Form.Item label={t(getKey('Номер телефона'))}>
            <CountryPhoneInput
              phonePrefix={uiStore.phoneModel.phonePrefix}
              phone={uiStore.phoneModel.phone}
              country={uiStore.phoneModel.selectedCountry.country}
              setPhone={uiStore.phoneModel.setPhone}
              setSelectedCountry={(value) => uiStore.phoneModel.setSelectedCountry(value ?? countries[0].value)}
            />
          </Form.Item>
        </Form>

        <Flex justify="start">
          <Button onClick={addEmployeeHandler} loading={loading}>
            {t(getSharedKey('next'))}
          </Button>
        </Flex>
      </Flex>
    </BgBox>
  )
})
